import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ImportResults = ({ results, onStartNewImport }) => {
  const { 
    totalProcessed, 
    successfulImports, 
    failedImports, 
    duplicatesSkipped,
    errors = [],
    processingTime 
  } = results;

  const successRate = totalProcessed > 0 ? (successfulImports / totalProcessed) * 100 : 0;

  return (
    <div className="bg-card rounded-lg border border-border">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground flex items-center">
              <Icon name="CheckCircle" size={20} className="mr-2 text-success" />
              Importação concluída
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Processamento concluído em {processingTime}s
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <Button variant="outline" onClick={onStartNewImport}>
              Importar outro arquivo
            </Button>
            <Link to="/member-list">
              <Button variant="default" iconName="Users" iconPosition="left">
                Ver membros
              </Button>
            </Link>
          </div>
        </div>
      </div>
      {/* Results Summary */}
      <div className="p-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="text-center p-4 bg-muted rounded-lg">
            <div className="text-2xl font-bold text-foreground">{totalProcessed}</div>
            <div className="text-sm text-muted-foreground">Total processado</div>
          </div>
          <div className="text-center p-4 bg-success/10 rounded-lg">
            <div className="text-2xl font-bold text-success">{successfulImports}</div>
            <div className="text-sm text-muted-foreground">Importados com sucesso</div>
          </div>
          <div className="text-center p-4 bg-error/10 rounded-lg">
            <div className="text-2xl font-bold text-error">{failedImports}</div>
            <div className="text-sm text-muted-foreground">Falhas na importação</div>
          </div>
          <div className="text-center p-4 bg-warning/10 rounded-lg">
            <div className="text-2xl font-bold text-warning">{duplicatesSkipped}</div>
            <div className="text-sm text-muted-foreground">Duplicados ignorados</div>
          </div>
        </div>

        {/* Success Rate */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">Taxa de sucesso</span>
            <span className="text-sm text-muted-foreground">{successRate?.toFixed(1)}%</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div 
              className="bg-success h-2 rounded-full transition-all duration-500"
              style={{ width: `${successRate}%` }}
            />
          </div>
        </div>

        {/* Error Details */}
        {errors?.length > 0 && (
          <div className="bg-error/5 border border-error/20 rounded-lg p-4">
            <h4 className="text-sm font-medium text-error mb-3 flex items-center">
              <Icon name="AlertCircle" size={16} className="mr-2" />
              Erros de importação ({errors?.length})
            </h4>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {errors?.map((error, index) => (
                <div key={index} className="text-xs text-muted-foreground bg-card p-2 rounded border">
                  <div className="font-medium">Linha {error?.row}:</div>
                  <div>{error?.message}</div>
                  {error?.data && (
                    <div className="mt-1 text-xs opacity-75">
                      Dados: {JSON.stringify(error?.data)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Success Message */}
        {successfulImports > 0 && (
          <div className="bg-success/5 border border-success/20 rounded-lg p-4 mt-4">
            <div className="flex items-center">
              <Icon name="CheckCircle" size={20} className="text-success mr-3" />
              <div>
                <h4 className="text-sm font-medium text-success">
                  {successfulImports} membros importados com sucesso!
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                  Novos membros foram adicionados à base e já estão disponíveis na lista.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Next Steps */}
        <div className="mt-6 p-4 bg-muted rounded-lg">
          <h4 className="text-sm font-medium text-foreground mb-3">Próximos passos</h4>
          <div className="space-y-2 text-sm text-muted-foreground">
            <div className="flex items-center">
              <Icon name="Users" size={16} className="mr-2" />
              <Link to="/member-list" className="hover:text-foreground contextual-fade">
                Revisar membros importados na lista
              </Link>
            </div>
            <div className="flex items-center">
              <Icon name="UserPlus" size={16} className="mr-2" />
              <Link to="/member-registration" className="hover:text-foreground contextual-fade">
                Adicionar membros individualmente
              </Link>
            </div>
            <div className="flex items-center">
              <Icon name="BarChart3" size={16} className="mr-2" />
              <Link to="/member-dashboard" className="hover:text-foreground contextual-fade">
                Ver estatísticas atualizadas do painel
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportResults;